/**
 * Shortest-path network interdiction — the Labyrinth Keeper engine.
 *
 * You *are* the dungeon. A hero walks from the entrance to the treasure along
 * the shortest 4-connected path on a grid. You spend a budget of walls to make
 * that journey as long as possible — but you can never seal the treasure off
 * completely (a labyrinth always has a way through). Choosing *which* walls,
 * knowing the hero will always re-route through the cheapest remaining path, is
 * a real, named Operations Research problem: **shortest-path network
 * interdiction** (Israeli & Wood, 2002) — a max–min bilevel program. The leader
 * (you) removes nodes to maximize the follower's (the hero's) shortest path.
 *
 * This module holds the *pure* logic: grid → graph, the hero's live shortest
 * path (BFS over unit steps), a myopic greedy baseline, a connectivity referee,
 * and the minimum vertex cut (so a level can guarantee `budget < min-cut`, i.e.
 * the hero can never be walled off and the optimum stays finite). The exact
 * max–min optimum is proved by a HiGHS MILP — see `interdictionModel.ts` and
 * `interdictionClient.ts`.
 */

/** A grid cell as [row, col]. */
export type GridCell = [number, number];

export interface InterdictionRequest {
  rows: number;
  cols: number;
  /** Permanent walls in the base layout (impassable, never counted in budget). */
  baseWalls?: GridCell[];
  /** The hero's entrance. */
  start: GridCell;
  /** The treasure. */
  goal: GridCell;
  /** How many extra walls the keeper may place. */
  budget: number;
  /** Solver wall-clock limit in seconds (default 30). */
  timeLimitSec?: number;
}

export interface InterdictionResult {
  /** "OPTIMAL" only when HiGHS proved it; otherwise "FEASIBLE" / other. */
  status: string;
  /** The forced shortest-path length (steps) under the chosen walls. */
  length: number;
  /** The cells the keeper walls off, as [row, col]. */
  walls: GridCell[];
  /** The hero's shortest path under those walls (cells, incl. start & goal). */
  path: GridCell[];
  tBuildMs: number;
  /** Pure solver time, measured by the caller around the solve. */
  tSolveMs: number;
}

/** Flatten a cell to an integer id. */
export function cellId(r: number, c: number, cols: number): number {
  return r * cols + c;
}

const key = (r: number, c: number) => r + "," + c;

/** The set of passable base cells (everything that is not a permanent wall). */
export function passableCells(req: InterdictionRequest): GridCell[] {
  const wall = new Set((req.baseWalls ?? []).map(([r, c]) => key(r, c)));
  const out: GridCell[] = [];
  for (let r = 0; r < req.rows; r++)
    for (let c = 0; c < req.cols; c++)
      if (!wall.has(key(r, c))) out.push([r, c]);
  return out;
}

const DIRS: GridCell[] = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];

/**
 * The hero's shortest path: BFS over unit steps from start to goal, treating
 * `baseWalls` and any cell in `blocked` as impassable. Returns the step length
 * and the path (cells incl. both ends), or `null` if the goal is unreachable.
 */
export function shortestPath(
  req: InterdictionRequest,
  blocked: Iterable<GridCell> = [],
): { length: number; path: GridCell[] } | null {
  const { rows, cols } = req;
  const impassable = new Set<string>();
  for (const [r, c] of req.baseWalls ?? []) impassable.add(key(r, c));
  for (const [r, c] of blocked) impassable.add(key(r, c));

  const [sr, sc] = req.start;
  const [gr, gc] = req.goal;
  if (impassable.has(key(sr, sc)) || impassable.has(key(gr, gc))) return null;

  const prev = new Int32Array(rows * cols).fill(-2); // -2 = unvisited, -1 = source
  const queue: number[] = [cellId(sr, sc, cols)];
  prev[cellId(sr, sc, cols)] = -1;
  const goalId = cellId(gr, gc, cols);

  for (let head = 0; head < queue.length; head++) {
    const id = queue[head];
    if (id === goalId) break;
    const r = Math.floor(id / cols);
    const c = id % cols;
    for (const [dr, dc] of DIRS) {
      const nr = r + dr;
      const nc = c + dc;
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      if (impassable.has(key(nr, nc))) continue;
      const nid = cellId(nr, nc, cols);
      if (prev[nid] !== -2) continue;
      prev[nid] = id;
      queue.push(nid);
    }
  }

  if (prev[goalId] === -2) return null;

  // walk back
  const path: GridCell[] = [];
  let cur = goalId;
  while (cur !== -1) {
    path.push([Math.floor(cur / cols), cur % cols]);
    cur = prev[cur];
  }
  path.reverse();
  return { length: path.length - 1, path };
}

/** Connectivity referee: are start and goal still connected given these walls? */
export function isConnected(req: InterdictionRequest, blocked: Iterable<GridCell> = []): boolean {
  return shortestPath(req, blocked) !== null;
}

/**
 * A myopic greedy baseline — the obvious play. Place each wall, one at a time,
 * on the passable non-terminal cell that maximizes the *immediate* increase in
 * the hero's shortest path, never disconnecting the goal. Deterministic
 * (lowest [row,col] wins ties), so a stored baseline is re-verifiable. It is
 * never claimed optimal; coordinating walls (the MILP) can do strictly better.
 */
export function greedyInterdiction(req: InterdictionRequest): { walls: GridCell[]; length: number } {
  const walls: GridCell[] = [];
  const wallSet = new Set<string>();
  const base = shortestPath(req);
  if (!base) return { walls, length: 0 };

  const terminals = new Set([key(...req.start), key(...req.goal)]);
  const candidates = passableCells(req).filter(([r, c]) => !terminals.has(key(r, c)));

  for (let step = 0; step < req.budget; step++) {
    let bestCell: GridCell | null = null;
    let bestLen = -1;
    for (const [r, c] of candidates) {
      if (wallSet.has(key(r, c))) continue;
      const trial = [...walls, [r, c] as GridCell];
      const sp = shortestPath(req, trial);
      if (!sp) continue; // would disconnect — never allowed
      if (sp.length > bestLen) {
        bestLen = sp.length;
        bestCell = [r, c];
      }
    }
    if (!bestCell) break;
    walls.push(bestCell);
    wallSet.add(key(...bestCell));
  }

  const sp = shortestPath(req, walls);
  return { walls, length: sp ? sp.length : 0 };
}

/**
 * Minimum vertex cut between start and goal (Menger): the fewest *intermediate*
 * cells whose removal disconnects the hero from the treasure. A level with
 * `budget < minVertexCut` can never be sealed off, so the interdiction optimum
 * is always a finite, real path length. Computed as a unit-capacity max-flow on
 * the node-split graph (each cell splits into in→out with capacity 1, except
 * start/goal which are uncuttable).
 */
export function minVertexCut(req: InterdictionRequest): number {
  const cells = passableCells(req);
  const idx = new Map<string, number>();
  cells.forEach(([r, c], i) => idx.set(key(r, c), i));
  const n = cells.length;

  // If either terminal is itself a wall, they are already disconnected.
  if (!idx.has(key(...req.start)) || !idx.has(key(...req.goal))) return 0;

  // Node-split: cell i → in=2i, out=2i+1. Terminals are uncuttable (cap ∞).
  const N = 2 * n;
  const INF = 1e9;
  // adjacency as arrays of [to, cap, revIndex]
  const to: number[] = [];
  const cap: number[] = [];
  const rev: number[] = [];
  const head: number[][] = Array.from({ length: N }, () => []);
  const addEdge = (u: number, v: number, cp: number) => {
    const f = to.length; // forward edge index
    to.push(v); cap.push(cp); rev.push(f + 1); // reverse will sit at f + 1
    head[u].push(f);
    const b = to.length; // reverse edge index (= f + 1)
    to.push(u); cap.push(0); rev.push(f);
    head[v].push(b);
  };

  const startKey = key(...req.start);
  const goalKey = key(...req.goal);
  cells.forEach(([r, c], i) => {
    const uncuttable = key(r, c) === startKey || key(r, c) === goalKey;
    addEdge(2 * i, 2 * i + 1, uncuttable ? INF : 1);
  });
  for (const [r, c] of cells) {
    const i = idx.get(key(r, c))!;
    for (const [dr, dc] of DIRS) {
      const nr = r + dr;
      const nc = c + dc;
      const j = idx.get(key(nr, nc));
      if (j === undefined) continue;
      addEdge(2 * i + 1, 2 * j, INF); // out_i → in_j
    }
  }

  const s = 2 * idx.get(startKey)! + 1; // start_out
  const t = 2 * idx.get(goalKey)!; // goal_in

  // Edmonds–Karp (BFS augmenting paths); unit/INF caps, small graph.
  let flow = 0;
  for (;;) {
    const prevEdge = new Int32Array(N).fill(-1);
    prevEdge[s] = -2;
    const q = [s];
    for (let h = 0; h < q.length && prevEdge[t] === -1; h++) {
      const u = q[h];
      for (const ei of head[u]) {
        if (cap[ei] <= 0) continue;
        const v = to[ei];
        if (prevEdge[v] !== -1) continue;
        prevEdge[v] = ei;
        q.push(v);
      }
    }
    if (prevEdge[t] === -1) break;
    // bottleneck along the augmenting path (the tail of edge ei is the head of
    // its reverse arc)
    let push = INF;
    for (let v = t; v !== s; v = to[rev[prevEdge[v]]]) {
      push = Math.min(push, cap[prevEdge[v]]);
    }
    for (let v = t; v !== s; v = to[rev[prevEdge[v]]]) {
      const ei = prevEdge[v];
      cap[ei] -= push;
      cap[rev[ei]] += push;
    }
    flow += push;
    if (flow >= INF) return flow; // shouldn't happen with B < cut design
  }
  return flow;
}
